import React, { useState, useRef } from 'react';
import { Paperclip, Upload, Trash2, Download, FileText, Image, File } from 'lucide-react';
import devIssueService from '../../../services/devIssueService';

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function FileIcon({ mimeType }) {
  if (mimeType?.startsWith('image/')) return <Image className="h-4 w-4 text-blue-500" />;
  if (mimeType?.includes('pdf')) return <FileText className="h-4 w-4 text-red-500" />;
  return <File className="h-4 w-4 text-gray-400" />;
}

const IssueAttachments = ({ issueId, attachments, onAttachmentsChange }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const res = await devIssueService.uploadAttachment(issueId, file);
      onAttachmentsChange([...attachments, res.data]);
    } catch {
      // silently fail
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (files) => {
    Array.from(files).forEach(uploadFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDelete = async (attachmentId) => {
    try {
      await devIssueService.deleteAttachment(issueId, attachmentId);
      onAttachmentsChange(attachments.filter(a => a.id !== attachmentId));
    } catch {
      // silently fail
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const res = await devIssueService.getDownloadUrl(issueId, attachment.id);
      window.open(res.data.download_url, '_blank');
    } catch {
      // silently fail
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Paperclip className="h-4 w-4 text-gray-500" />
        <h4 className="text-sm font-semibold text-gray-700">
          Attachments {attachments.length > 0 && <span className="text-gray-400 font-normal">({attachments.length})</span>}
        </h4>
      </div>

      {/* File list */}
      {attachments.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {attachments.map(a => (
            <div key={a.id} className="group flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
              <FileIcon mimeType={a.mime_type} />
              <span className="text-sm text-gray-700 flex-1 truncate">{a.original_file_name}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">{formatBytes(a.file_size)}</span>
              <button
                onClick={() => handleDownload(a)}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-blue-500"
                title="Download"
              >
                <Download className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handleDelete(a.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500"
                title="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
          dragActive ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
        onDragEnter={() => setDragActive(true)}
        onDragLeave={() => setDragActive(false)}
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="h-4 w-4 text-gray-400 mx-auto mb-1" />
        <p className="text-xs text-gray-500">
          {uploading ? 'Uploading…' : 'Drop files here or click to upload'}
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={e => handleFiles(e.target.files)}
        />
      </div>
    </div>
  );
};

export default IssueAttachments;
